// Sale model
import date from "date-and-time";

import saleLine from "./SaleLine";
import MenuRest from "./components/MenuRest";
import TableDisplay from "./components/TableDisplay";
import dates from "tools/dates";
import proxy from "api/proxy";
import { getStorage } from "store/local";
import funcs from "tools/functions";
import PritingAPI from "tools/printing";
import useMainStore from "store/mainStore";
import { useStoreRest } from "./storeRest";

const stateColors = {
  draft: "amber",
  quotation: "sky",
  processing: "lime",
  cancelled: "rose",
  // cancelled: "indigo",
};

const defaultParty = async () => {
  const session = getStorage("ctxSession");
  const dom = [["id", "=", session.shop]];
  const fields = ["id", "rec_name", "party"];
  const { data } = await proxy.search("sale.shop", dom, fields, 1);
  return data[0].party;
};

const defaultAgent = async () => {
  const session = getStorage("ctxSession");
  const dom = [["user", "=", session.user]];
  const fields = ["id", "rec_name"];
  const { data } = await proxy.search("commission.agent", dom, fields, 1);
  return data[0];
};

const getFilters = () => {
  // const session = getStorage("ctxSession");
  let last14 = date.addHours(new Date(), -14);
  last14 = dates.fmtDatetime2Tryton(last14);
  return {
    today: [
      // ["create_uid", "=", session.user],
      ["create_date", ">=", last14],
    ],
    draft: [
      ["state", "=", "draft"],
      ["create_date", ">=", last14],
    ],
    reservation: [
      ["state", "in", ["draft", "quotation"]],
      ["kind", "=", "reservation"],
    ],
  };
};

const getTable = (value) => {
  const session = getStorage("ctxSession");
  let dom = [
    ["shop", "=", session.shop],
    // ["state", "=", "available"],
  ];
  if (value) {
    const addDom = ["name", "ilike", `%${value}%`];
    dom.push(addDom);
  }
  return dom;
};

function setReadOnly(rec) {
  return rec.number ? true : false;
}

function readOnlyLines(rec) {
  let res = false;
  if (rec.state === "quotation") {
    res = true;
  }
  return res;
}

function isVisibleSend(name, store) {
  let res = false;
  if (store.lines) {
    for (const line of store.lines.values()) {
      if (line.id <= 0 || line.status_order === "draft") {
        res = true;
      }
    }
  }
  if (store?.id < 0 && !store.table_assigned) {
    res = false;
  }
  return res;
}

const getTableScreen = (props) => {
  return TableDisplay(props);
};

function visibleMenu(name, record) {
  return record?.state === "draft" && record?.shop > 0;
}

const getTotalAmount = (record) => {
  const lines = record?.lines?.values();
  if (lines) {
    const totalAmount = Array.from(lines).reduce((total, item) => {
      return total + item.quantity * item.amount_w_tax;
    }, 0);
    // return func.fmtMoney(totalAmount);
    return totalAmount;
  }
};

async function sendOrder(record) {
  const session = getStorage("ctxSession");
  let ctx = {};

  if (session.agentUser) {
    ctx = { user: session.agentUser };
  }

  const { store, context, setRecord } = useMainStore.getState();
  const storeData = funcs.recToTryton(store);

  const res = await proxy.methodCall({
    model: "sale.sale",
    method: "send_order",
    args: [storeData],
    ctx,
  });
  if (res.error) return res;

  // Reload record so number, state and lines status are shown in the form
  const recId = res.data?.record?.id ?? record?.id;
  if (recId > 0) {
    await setRecord(recId, "sale.sale", context.ctxView);
  }
  return res;
}

async function print_order(record) {
  if (!record?.id) {
    window.alert("No hay una venta válida para imprimir.");
    return;
  }

  try {
    const session = getStorage("ctxSession");

    const [{ data: ctxPrinting }, { data: sale }] = await Promise.all([
      proxy.methodCall({
        model: "sale.sale",
        method: "get_printing_context",
        args: [{ user_id: session.user }],
      }),
      proxy.methodCall({
        model: "sale.sale",
        method: "get_data",
        args: [{ type_doc: "order", sale_id: record.id }],
      }),
    ]);

    const store = useStoreRest.getState();
    const shop = store.shops?.[record.shop];
    const apiPrint = ctxPrinting?.local_printer || shop?.local_printer;

    if (!apiPrint) {
      window.alert(
        "No se encontró la URL de impresión local para esta sucursal.",
      );
      return;
    }

    const payload = {
      context: {
        ...ctxPrinting,
        local_printer: apiPrint,
      },
      record: {
        ...sale,
      },
      type_doc: "order",
    };

    const printer = new PritingAPI(apiPrint);
    const { data, error } = await printer.post("/print_invoice", payload);

    if (error || !data) {
      window.alert(error || "LA IMPRESION DE FACTURA FALLÓ!");
      return;
    }

    window.alert("Factura enviada a la impresora correctamente.");
  } catch (error) {
    console.error("Error printing invoice", error);
    window.alert("No fue posible enviar la factura a impresión.");
  }
}

function isVisiblePrint(name, record) {
  const shops = useStoreRest.getState().shops;
  const shop = shops[record.shop];
  if (shop?.local_printer) {
    return true;
  }
  return false;
}

const getView = (config) => {
  const session = getStorage("ctxSession");
  const DictCtxView = {
    selectable: false, // options: multi - one - false
    activeSearch: true,
    filters: getFilters,
    access: { edit: () => false },
    form_action: ["edit"],
    table_action: ["edit", "add"],
    model: "sale.sale",
    preSave: "send_order",
    title: { field: "number" },
    tags: {
      state: stateColors,
    },
    webfields: {
      number: { type: "char", readOnly: true, searchable: true },
      party: {
        type: "many2one",
        model: "party.party",
        default: defaultParty,
        searchable: true,
      },
      table_assigned: {
        type: "many2one",
        recName: "name",
        model: "sale.shop.table",
        recSearch: getTable,
        required: true,
        component: getTableScreen,
        searchable: true,
      },
      shop: {
        type: "char", // defined type char for see_menu, this field is not used in view
        default: session.shop,
        visible: () => false,
      },
      agent: {
        type: "many2one",
        model: "commission.agent",
        recSearch: () => [["user", "=", session.user]],
        default: defaultAgent,
        readOnly: setReadOnly,
        searchable: true,
      },
      sale_date: {
        type: "date",
        readOnly: true,
        searchable: true,
      },
      persons: {
        type: "integer",
        default: 2,
        // readOnly: true,
      },
      price_list: {
        type: "many2one",
        model: "product.price_list",
        recSearch: () => "[]",
        readOnly: { state: ["quotation"] },
      },
      state: {
        type: "char",
        readOnly: true,
        translate: true,
        default: "draft",
        tags: stateColors,
      },
      kind: {
        type: "char",
        readOnly: true,
        default: "to_table",
      },
      lines: {
        type: "one2many",
        model: "sale.line",
        ctxView: saleLine.ctxView(config),
        required: true,
        readOnly: readOnlyLines,
        // default: [],
      },
      total_amount: {
        type: "numeric",
        readOnly: true,
        function: getTotalAmount,
      },
      comment: { type: "text", readOnly: { state: ["quotation"] } },
      send_order: {
        type: "button",
        method: sendOrder,
        color: "green",
        visible: isVisibleSend,
      },
      print_order: {
        type: "button",
        method: print_order,
        color: "amber",
        visible: isVisiblePrint,
      },
      // change_table: {
      //   type: "button",
      //   method: "send_order",
      //   color: "amber",
      //   visible: isVisibleChangeTable,
      // },
      see_menu: {
        type: "button_custom_modal",
        Component: MenuRest,
        show_title: false,
        visible: visibleMenu,
        icon: "fi fi-rr-edit",
        color: "amber",
      },
    },
    webtree: [
      { name: "number", width: "15%" },
      { name: "table_assigned", width: "30%" },
      { name: "agent" },
      // { name: "total_amount", width: "10%" },
      { name: "sale_date", width: "15%" },
      {
        name: "state",
        width: "15%",
        widget: "badge",
      },
    ],
    webform: [
      { name: "agent" },
      { name: "shop" },
      {
        id: "info_order",
        grid: [
          { name: "persons", colSpan: "col-span-1" },
          { name: "number", colSpan: "col-span-1" },
        ],
        cols: "grid-cols-2",
        colSpan: "col-span-2 md:col-span-1",
      },
      { name: "table_assigned", widget: "custom" },
      { name: "see_menu" },
      { name: "lines" },
      { name: "total_amount" },
      { name: "send_order" },
      { name: "print_order" },
      // { name: "change_table" },
      { name: "state" },
      { name: "comment" },
    ],
  };

  return DictCtxView;
};

export default { ctxView: getView };
